import React, { useState, useEffect, useRef, useCallback } from 'react';
import { withTheme } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useImageHistory } from 'components/hooks';
import { MESSAGE_TYPES } from 'components/notifications';
import { createRpc } from 'store/actions/rpc';
import { getUserData } from 'store/actions/userData';
import { addMessage } from 'store/actions/app';
import { wssCallback } from 'utils/jobSockets';
import {
  removeImageBackground,
  makeImagePhotorealistic,
  urlToImageFile,
  startMeshyImageTo3D,
  getMeshyImageTo3DStatus,
} from 'utils/aiTools';
import {
  PageContainer,
  ContentArea,
  CanvasColumn,
  ModalMessageTitle,
} from './imageCanvasStyles';
import { TopCanvasToolbar } from './components/TopCanvasToolbar';
import { ImagesSidebar } from './components/ImagesSidebar';
import { ImageUploadCanvas } from './components/ImageUploadCanvas';
import { EditToolsSidebar } from './components/EditToolsSidebar';
import { SimpleModePanel } from './components/SimpleModePanel';
import { Generate3DOverlay } from './components/Generate3DOverlay';
import { validateSimpleGenerate, buildSimplePayload } from './simpleGenerate';
import { parseRpcGuid, resolveGuidPreview } from './rpcGuid';

const ACCEPTED_TYPES = [ 'image/png', 'image/jpeg', 'image/jpg', 'image/webp' ];
const ZOOM_STEP = 25;
const ZOOM_MIN = 25;
const ZOOM_MAX = 300;
const SUCCESS_STATUS_MS = 2000;
const MESHY_POLL_INTERVAL_MS = 3000;
const PROVIDER_UPLOAD_MAX = { tripo: 4, meshy: 4, rodin: 5, tencent: 4 };
const PROVIDER_SUBMIT_MAX = { tencent: 1 };
const VIEW_NAMES = [ 'model', 'left', 'back', 'right' ];

const ImageCanvas = withTheme(({ theme }) => {
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const [ cookies ] = useCookies([ 'userAuth' ]);
  const token = cookies.archvision_token;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    images,
    activeId,
    setActiveId,
    active,
    current,
    canUndo,
    canRedo,
    canReset,
    addImages,
    removeImage,
    applyEdit,
    undo,
    redo,
    reset,
    maxImages,
  } = useImageHistory();

  const imageName = active?.name || 'Untitled Image';
  const [ provider, setProvider ] = useState(() => {
    const stored = localStorage.getItem('imageCanvasProvider');
    const allowed = [ 'meshy', 'tripo', 'rodin', 'tencent' ];
    return allowed.includes(stored) ? stored : 'meshy';
  });
  const [ selectedModel, setSelectedModel ] = useState('meshy');
  const [ zoom, setZoom ] = useState(100);
  const [ isDragOver, setIsDragOver ] = useState(false);
  const [ error, setError ] = useState(null);
  const [ selectedTool, setSelectedTool ] = useState(null);
  const [ status, setStatus ] = useState(null);
  const [ aiBusyTool, setAiBusyTool ] = useState(null);
  const [ generation, setGeneration ] = useState(null);
  const [ isDownloading, setIsDownloading ] = useState(false);
  const [ isSimpleSubmitting, setIsSimpleSubmitting ] = useState(false);
  const [ goToGenerate, setGoToGenerate ] = useState(false);

  const pollTimerRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const isUnmountedRef = useRef(false);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    isUnmountedRef.current = true;
    clearPollTimer();
  }, [ clearPollTimer ]);

  const ownedBlobUrlsRef = useRef([]);
  const trackBlobUrl = useCallback(url => {
    if (url?.startsWith('blob:')) ownedBlobUrlsRef.current.push(url);
  }, []);

  useEffect(() => () => {
    ownedBlobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    ownedBlobUrlsRef.current = [];
  }, []);

  useEffect(() => {
    if (status?.variant === 'success' || status?.variant === 'error') {
      const timer = setTimeout(() => setStatus(null), SUCCESS_STATUS_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [ status ]);

  const showStatus = useCallback((message, variant = 'info') => {
    setStatus({ message, variant });
  }, []);

  useEffect(() => {
    localStorage.setItem('imageCanvasProvider', provider);
  }, [ provider ]);

  const effectiveMaxImages = PROVIDER_UPLOAD_MAX[provider] ?? maxImages;

  useEffect(() => {
    if (images.length === 0) setGoToGenerate(false);
  }, [ images.length ]);

  const handleFiles = useCallback(files => {
    const list = Array.isArray(files) ? files : [ files ];
    const accepted = list.filter(f => f && ACCEPTED_TYPES.includes(f.type));
    const rejectedCount = list.length - accepted.length;

    if (accepted.length === 0) {
      setError('Please upload a PNG, JPG, JPEG, or WEBP image.');
      showStatus('Unsupported file type.', 'error');
      return;
    }

    const room = effectiveMaxImages - images.length;
    const toAdd = accepted.slice(0, Math.max(0, room));
    const overflowCount = accepted.length - toAdd.length;

    if (toAdd.length === 0) {
      showStatus(`You can upload up to ${effectiveMaxImages} image${effectiveMaxImages === 1 ? '' : 's'}.`, 'error');
      return;
    }

    const payload = toAdd.map(f => {
      const src = URL.createObjectURL(f);
      trackBlobUrl(src);
      return { src, name: f.name.replace(/\.[^.]+$/, '') || 'Untitled Image' };
    });

    setError(null);
    setZoom(100);
    addImages(payload);

    if (rejectedCount > 0) {
      showStatus(`${rejectedCount} file(s) skipped: unsupported format.`, 'info');
    } else if (overflowCount > 0) {
      showStatus(`Added ${toAdd.length}. ${overflowCount} extra image(s) skipped (max ${effectiveMaxImages}).`, 'info');
    } else {
      showStatus(`Added ${toAdd.length} image${toAdd.length === 1 ? '' : 's'}.`, 'success');
    }
  }, [ images.length, effectiveMaxImages, addImages, showStatus, trackBlobUrl ]);

  // Only a lone file we could not have opened anyway gets treated as an RPC GUID reference, so a
  // drop that works today keeps working and the normal path makes no extra request.
  const handleDroppedFiles = useCallback(async files => {
    const guid = files.length === 1 && !ACCEPTED_TYPES.includes(files[0].type)
      ? parseRpcGuid(files[0].name)
      : null;

    if (!guid) {
      handleFiles(files);
      return;
    }

    if (images.length >= effectiveMaxImages) {
      showStatus(`You can upload up to ${effectiveMaxImages} image${effectiveMaxImages === 1 ? '' : 's'}.`, 'error');
      return;
    }

    setError(null);
    showStatus('Loading model preview…', 'info');

    try {
      const preview = await resolveGuidPreview(guid, token);
      if (isUnmountedRef.current) return;
      if (!preview) throw new Error('No preview found for this model.');

      const file = await urlToImageFile(preview.url, preview.name);
      // Bail before creating the blob URL: after unmount the cleanup that revokes tracked URLs has
      // already run, so anything added here would be retained for the life of the tab.
      if (isUnmountedRef.current) return;

      const src = URL.createObjectURL(file);
      trackBlobUrl(src);

      setZoom(100);
      addImages([ { src, name: preview.name } ]);
      showStatus('Model preview added.', 'success');
    } catch {
      if (isUnmountedRef.current) return;
      showStatus('No preview found for this model.', 'error');
    }
  }, [ handleFiles, images.length, effectiveMaxImages, token, addImages, showStatus, trackBlobUrl ]);

  const handleReplaceImage = useCallback((id, file) => {
    if (!file || !ACCEPTED_TYPES.includes(file.type)) {
      setError('Please upload a PNG, JPG, JPEG, or WEBP image.');
      showStatus('Unsupported file type.', 'error');
      return;
    }

    const src = URL.createObjectURL(file);
    trackBlobUrl(src);
    setError(null);
    setZoom(100);
    applyEdit(id, src);
    setActiveId(id);
    showStatus('Image replaced.', 'success');
  }, [ applyEdit, setActiveId, showStatus, trackBlobUrl ]);

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - ZOOM_STEP, ZOOM_MIN));

  const handleRemoveBackground = useCallback(async () => {
    if (aiBusyTool) return;

    if (!active) {
      setError('Please upload an image first.');
      showStatus('Please upload an image first.', 'error');
      return;
    }

    if (!token) {
      showStatus('You need to be signed in to use AI tools.', 'error');
      return;
    }

    const targetId = active.id;
    const sourceUrl = active.current;
    const sourceName = active.name;

    setAiBusyTool('remove-bg');
    setError(null);
    showStatus('Removing background...', 'info');

    try {
      const file = await urlToImageFile(sourceUrl, sourceName);
      const resultFile = await removeImageBackground({ file, token });

      const imageUrl = URL.createObjectURL(resultFile);
      trackBlobUrl(imageUrl);

      applyEdit(targetId, imageUrl);
      showStatus('Background removed successfully.', 'success');
    } catch (err) {
      if (err.code === 'moderation_blocked') {
        dispatch(addMessage([ {
          message: (
            <div>
              <ModalMessageTitle>Background removal failed</ModalMessageTitle>
              {err.message}
            </div>
          ),
          config: { type: MESSAGE_TYPES.warning, showModal: true },
        } ]));
        setStatus(null);
      } else {
        showStatus(err.message || 'Failed to remove background.', 'error');
      }
    } finally {
      setAiBusyTool(null);
    }
  }, [ aiBusyTool, active, token, applyEdit, showStatus, trackBlobUrl, dispatch ]);

  const handleEnhance = useCallback(async () => {
    if (aiBusyTool) return;

    if (!active) {
      setError('Please upload an image first.');
      showStatus('Please upload an image first.', 'error');
      return;
    }

    if (!token) {
      showStatus('You need to be signed in to use AI tools.', 'error');
      return;
    }

    const targetId = active.id;
    const sourceUrl = active.current;
    const sourceName = active.name;

    setAiBusyTool('enhance');
    setError(null);
    showStatus('Enhancing image...', 'info');

    try {
      const file = await urlToImageFile(sourceUrl, sourceName);
      const resultFile = await makeImagePhotorealistic({ file, token });

      const imageUrl = URL.createObjectURL(resultFile);
      trackBlobUrl(imageUrl);

      applyEdit(targetId, imageUrl);
      showStatus('Image enhanced successfully.', 'success');
    } catch (err) {
      showStatus(err.message || 'Failed to enhance image.', 'error');
    } finally {
      setAiBusyTool(null);
    }
  }, [ aiBusyTool, active, token, applyEdit, showStatus, trackBlobUrl ]);

  const handleToolSelect = id => {
    if (aiBusyTool) return;
    if (id === 'remove-bg') {
      handleRemoveBackground();
      return;
    }
    if (id === 'enhance') {
      handleEnhance();
      return;
    }
    setSelectedTool(prev => (prev === id ? null : id));
  };

  const finishEdit = useCallback(dataUrl => {
    if (!activeId) return;
    trackBlobUrl(dataUrl);
    applyEdit(activeId, dataUrl);
    setSelectedTool(null);
    showStatus('Image updated successfully.', 'success');
  }, [ activeId, applyEdit, showStatus, trackBlobUrl ]);

  const handleCropApply = useCallback(dataUrl => finishEdit(dataUrl), [ finishEdit ]);
  const handleCropCancel = useCallback(() => setSelectedTool(null), []);
  const handleRotateApply = useCallback(dataUrl => finishEdit(dataUrl), [ finishEdit ]);
  const handleRotateCancel = useCallback(() => setSelectedTool(null), []);
  const handleAdjustApply = useCallback(dataUrl => finishEdit(dataUrl), [ finishEdit ]);
  const handleResizeApply = useCallback(dataUrl => finishEdit(dataUrl), [ finishEdit ]);
  const handleResizeCancel = useCallback(() => setSelectedTool(null), []);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;

    if (!current) {
      showStatus('Please upload an image first.', 'error');
      return;
    }

    setIsDownloading(true);
    try {
      const file = await urlToImageFile(current, imageName);
      if (!file) throw new Error('Could not read the current image.');

      const downloadUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      showStatus('Image downloaded.', 'success');
    } catch (err) {
      showStatus(err.message || 'Failed to download image.', 'error');
    } finally {
      setIsDownloading(false);
    }
  }, [ isDownloading, current, imageName, showStatus ]);

  const handleResetEdits = useCallback(() => {
    reset();
    setSelectedTool(null);
    showStatus('Image updated successfully.', 'success');
  }, [ reset, showStatus ]);

  const stopGeneration = useCallback(() => {
    clearPollTimer();
    isGeneratingRef.current = false;
  }, [ clearPollTimer ]);

  const pollMeshyStatus = useCallback(async taskId => {
    if (isUnmountedRef.current || !isGeneratingRef.current) return;

    try {
      const result = await getMeshyImageTo3DStatus({ taskId, token });
      if (isUnmountedRef.current || !isGeneratingRef.current) return;

      const { status: taskStatus, progress: taskProgress, glbUrl, error: taskError } = result || {};

      setGeneration(prev => prev && prev.taskId === taskId
        ? { ...prev, status: taskStatus, progress: taskProgress ?? prev.progress }
        : prev);

      if (taskStatus === 'SUCCEEDED') {
        stopGeneration();
        if (!glbUrl) {
          setGeneration({
            taskId,
            status: 'FAILED',
            progress: taskProgress ?? 100,
            error: 'Generation finished but no model URL was returned.',
          });
          return;
        }
        navigate('/create', {
          state: { glbUrl },
        });
        return;
      }

      if (taskStatus === 'FAILED' || taskStatus === 'CANCELED') {
        stopGeneration();
        const message = taskError
          || (taskStatus === 'CANCELED' ? 'Generation was canceled.' : 'Generation failed.');
        setGeneration({
          taskId, status: taskStatus, progress: taskProgress ?? 0, error: message,
        });
        return;
      }

      pollTimerRef.current = setTimeout(() => pollMeshyStatus(taskId), MESHY_POLL_INTERVAL_MS);
    } catch (err) {
      if (isUnmountedRef.current) return;
      stopGeneration();
      setGeneration({
        taskId,
        status: 'FAILED',
        progress: 0, error: err.message || 'Failed to check generation status.',
      });
    }
  }, [ token, navigate, imageName, stopGeneration ]);

  const handleGenerate = useCallback(async () => {
    if (isGeneratingRef.current || aiBusyTool) return;

    if (images.length === 0) {
      setError('Please upload an image first.');
      showStatus('Please upload an image first.', 'error');
      return;
    }

    if (!token) {
      showStatus('You need to be signed in to generate a 3D model.', 'error');
      return;
    }

    isGeneratingRef.current = true;
    setError(null);
    setGeneration({ taskId: null, status: 'STARTED', progress: 0, error: null });

    try {
      const files = await Promise.all(
        images.map(img => urlToImageFile(img.current, img.name)),
      );
      if (files.some(f => !f)) throw new Error('Could not read one of the images.');

      const { taskId, status: initialStatus, progress: initialProgress } = await startMeshyImageTo3D({ files, token });
      if (isUnmountedRef.current) return;

      if (!taskId) throw new Error('Server did not return a task id.');

      setGeneration({
        taskId,
        status: initialStatus || 'STARTED',
        progress: initialProgress ?? 0,
        error: null,
      });

      pollTimerRef.current = setTimeout(() => pollMeshyStatus(taskId), MESHY_POLL_INTERVAL_MS);
    } catch (err) {
      if (isUnmountedRef.current) return;
      stopGeneration();
      setGeneration({
        taskId: null,
        status: 'FAILED',
        progress: 0,
        error: err.message || 'Failed to start 3D generation.',
      });
    }
  }, [ aiBusyTool, images, token, showStatus, pollMeshyStatus, stopGeneration ]);

  const handleSimpleGenerate = useCallback(async ({
    name, category, tags, proxy, formats, debug, tencentSettings, meshySettings, tripoSettings, rodinSettings,
  }) => {
    if (isSimpleSubmitting) return;

    const validation = validateSimpleGenerate({ imageCount: images.length, token });
    if (!validation.valid) {
      showStatus(validation.message, 'error');
      return;
    }

    setIsSimpleSubmitting(true);
    dispatch(addMessage([ {
      message: 'Packaging model…',
      config: { type: MESSAGE_TYPES.loading, showModal: true },
    } ]));

    try {
      const submitLimit = PROVIDER_SUBMIT_MAX[provider] ?? images.length;
      const files = await Promise.all(
        images.slice(0, submitLimit).map(
          (img, index) => urlToImageFile(img.current, VIEW_NAMES[index] || img.name),
        ),
      );
      if (files.some(f => !f)) throw new Error('Could not read one of the images.');

      const payload = buildSimplePayload({
        provider,
        files,
        userData: userDataStore,
        meta: {
          name, category, tags, proxy, formats, tencentSettings, meshySettings, tripoSettings, rodinSettings,
        },
      });

      const wssArgs = {
        token,
        dispatch,
        userModels: userDataStore.userModels,
        handleCreditUpdate: () => dispatch(getUserData(token)),
      };

      dispatch(createRpc({
        token,
        userId: userDataStore?.data?.userId,
        userEmail: userDataStore?.data?.email,
        payload,
        debugState: debug,
        handleSockets: () => {
          wssCallback(wssArgs);
          navigate('/jobs');
        },
        onComplete: () => {
          if (!isUnmountedRef.current) setIsSimpleSubmitting(false);
        },
      }));
    } catch (err) {
      setIsSimpleSubmitting(false);
      showStatus(err.message || 'Failed to start 3D generation.', 'error');
    }
  }, [ isSimpleSubmitting, images, token, provider, userDataStore, dispatch, showStatus, navigate ]);

  const handleCloseGeneration = useCallback(() => {
    stopGeneration();
    setGeneration(null);
  }, [ stopGeneration ]);

  const isGenerating = Boolean(generation) && generation.status !== 'FAILED' && generation.status !== 'CANCELED';

  const shared = { theme, $selectedTheme: selectedTheme };
  const hasImage = Boolean(current);
  const getCurrentStep = () => {
    if (!hasImage) return 'upload';
    return goToGenerate ? 'generate' : 'edit';
  };
  const currentStep = getCurrentStep();

  return (
    <PageContainer {...shared}>
      <TopCanvasToolbar
        theme={theme}
        selectedTheme={selectedTheme}
        imageName={imageName}
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        currentStep={currentStep}
      />

      <ContentArea {...shared}>
        <ImagesSidebar
          theme={theme}
          selectedTheme={selectedTheme}
          images={images}
          activeId={activeId}
          onSelect={setActiveId}
          onRemove={removeImage}
          onFiles={handleFiles}
          onReplace={handleReplaceImage}
        />

        <CanvasColumn {...shared}>
          <ImageUploadCanvas
            theme={theme}
            selectedTheme={selectedTheme}
            previewUrl={current}
            zoom={zoom}
            isDragOver={isDragOver}
            error={error}
            status={status}
            onFiles={handleFiles}
            onDropFiles={handleDroppedFiles}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            selectedTool={selectedTool}
            canUndo={canUndo}
            canRedo={canRedo}
            canReset={canReset}
            onUndo={undo}
            onRedo={redo}
            onResetEdits={handleResetEdits}
            onCropApply={handleCropApply}
            onCropCancel={handleCropCancel}
            onRotateApply={handleRotateApply}
            onRotateCancel={handleRotateCancel}
            onAdjustApply={handleAdjustApply}
            onResizeApply={handleResizeApply}
            onResizeCancel={handleResizeCancel}
            onEditImage={currentStep === 'generate' ? () => setGoToGenerate(false) : undefined}
          />
        </CanvasColumn>

        {currentStep === 'generate' && (
          <SimpleModePanel
            theme={theme}
            selectedTheme={selectedTheme}
            hasImage={hasImage}
            imageName={imageName}
            provider={provider}
            onProviderChange={setProvider}
            isGenerating={isSimpleSubmitting}
            onGenerate={handleSimpleGenerate}
          />
        )}

        {currentStep === 'edit' && (
          <EditToolsSidebar
            theme={theme}
            selectedTheme={selectedTheme}
            hasImage={hasImage}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            selectedTool={selectedTool}
            onToolSelect={handleToolSelect}
            onNext={() => setGoToGenerate(true)}
            onGenerate={handleGenerate}
            onDownload={handleDownload}
            isDownloading={isDownloading}
            aiBusyTool={aiBusyTool}
            isGenerating={isGenerating}
          />
        )}

        {generation && (
          <Generate3DOverlay
            theme={theme}
            selectedTheme={selectedTheme}
            status={generation.status}
            progress={generation.progress}
            error={generation.error}
            onClose={handleCloseGeneration}
          />
        )}
      </ContentArea>
    </PageContainer>
  );
});

export { ImageCanvas };
